import {TreeNode} from 'primeng/api';
import {
  BudgetVsActualData,
  BudgetVsActualNode,
  BudgetVsActualNodePeriod,
  BvAHierarchyModel
} from '../models/budget-vs-actual-data.model';
import * as reducers from '../reducers/budget-vs-actual.reducers';
import {createFeatureSelector, createSelector} from "@ngrx/store";
import {distinct, distinctByProperty, groupAndSum, groupBy, groupByProp, sum} from '@common/index';
import {defaultSortBehaviour} from '@common/primeng/default-sort';
import {getHierarchies} from './budget-vs-actual-config.selectors';

export const budgetVsActualSelector = createFeatureSelector<reducers.State>('budgetVsActual');

const {selectEntities} = reducers.adapter.getSelectors(budgetVsActualSelector);

export const selectEntityByWidgetId = (widgetId: number) =>
  createSelector(selectEntities, entities => entities[widgetId]);

export const isLoading = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), entity => entity ? entity.loading || entity.loadingColumns : false);

export const getData = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;

    if (entity.data) {
      let data = entity.data;
      return data;
    }

    return undefined;
  });

export const getSelectedBudget = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading || !entity.selectedBudgetForChart) return {
      BudgetField: "Budget1",
      VarianceField: "VarianceAmount1"
    };
    return entity.selectedBudgetForChart;
  });

export const getSelectedPeriods = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.periods;
  })

  export const getCurrencyCode = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.currencyCode;
  });

export const getIncludeCommitments = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.includeCommitments;
  });

  export const getSelectedSchedules = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.scheduleRefs;
  });

export const getPropertyRef = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.propertyRef;
  });

export const getUnitRef = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.unitRef;
  });

export const getPropertyName = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.propertyName;
  });

export const getYear = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.year;
  });

export const getFilteredData = (widgetId: number) =>
  createSelector(getData(widgetId), getSelectedSchedules(widgetId), (data, scheduleRefs) => {
    return scheduleRefs && scheduleRefs.length > 0 ? data.filter(x => scheduleRefs.includes(x.ScheduleRef)) : data;
  });

export const fieldHasValue = (widgetId: number, field: keyof BudgetVsActualData) =>
  createSelector(getData(widgetId), (data) => {
    if (data == undefined) return true;
    return data?.findIndex(x => Number(x[field]) > 0) >= 0;
  });

const variancePercentageCalc = (budget: number, varianceAmount: number) => {
  return budget && varianceAmount ? varianceAmount / budget * 100 : 0;
}

const mapPeriodNodeData = (data: BudgetVsActualData[],
                           name: string,
                           lowBudgetThreshold: number,
                           overBudgetThreshold: number,
                           includeCommitments: boolean): BudgetVsActualNodePeriod => {
  const actual = sum(data, 'Actual');
  const previousYearActual = sum(data, 'PreviousYearActual');
  const budget1 = sum(data, 'Budget1');
  const budget2 = sum(data, 'Budget2');
  const budget3 = sum(data, 'Budget3');
  const commitmentSum = includeCommitments ? sum(data, 'Commitment') : 0;
  const commitment = commitmentSum < 0 ? 0 : commitmentSum;
  const actualPlusCommitment = includeCommitments ? sum(data, 'ActualPlusCommitment') : 0;
  const varianceAmount1 = includeCommitments
    ? sum(data, 'VarianceAmount1') + commitment
    : sum(data, 'VarianceAmount1');
  const varianceAmount2 = includeCommitments
    ? sum(data, 'VarianceAmount2') + commitment
    : sum(data, 'VarianceAmount2');
  const varianceAmount3 = includeCommitments
    ? sum(data, 'VarianceAmount3') + commitment
    : sum(data, 'VarianceAmount3');
  const variancePercentage1 = variancePercentageCalc(budget1, varianceAmount1);
  const variancePercentage2 = variancePercentageCalc(budget2, varianceAmount2);
  const variancePercentage3 = variancePercentageCalc(budget3, varianceAmount3);
  const apportionmentPercent: number = data?.length > 0 ? data[0].ApportionmentPercent : 0;
  const propTaskUri = data?.length > 0 ? data[0].PropTaskUri : 0;
  const propExpSchedUri = data?.length > 0 ? data[0].PropExpSchedUri : 0;
  const propBudPeriodFromDate = data?.length > 0 ? data[0].PropBudPeriodFromDate : null;
  const propBudPeriodToDate = data?.length > 0 ? data[0].PropBudPeriodToDate : null;

  return {
    Name: name,
    Actual: actual,
    PreviousYearActual: previousYearActual,
    Budget1: budget1,
    Budget2: budget2,
    Budget3: budget3,
    VarianceAmount1: varianceAmount1,
    VarianceAmount2: varianceAmount2,
    VarianceAmount3: varianceAmount3,
    VariancePercent1: variancePercentage1,
    VariancePercent2: variancePercentage2,
    VariancePercent3: variancePercentage3,
    OverBudgetThreshold1: variancePercentage1 > overBudgetThreshold,
    OverBudgetThreshold2: variancePercentage2 > overBudgetThreshold,
    OverBudgetThreshold3: variancePercentage3 > overBudgetThreshold,
    LowBudgetThreshold1: budget1 > 0 && variancePercentage1 >= (lowBudgetThreshold * -1) && variancePercentage1 <= overBudgetThreshold,
    LowBudgetThreshold2: budget2 > 0 && variancePercentage2 >= (lowBudgetThreshold * -1) && variancePercentage2 <= overBudgetThreshold,
    LowBudgetThreshold3: budget3 > 0 && variancePercentage3 >= (lowBudgetThreshold * -1) && variancePercentage3 <= overBudgetThreshold,
    Commitment: commitment,
    ActualPlusCommitment: actualPlusCommitment,
    ApportionmentPercent: apportionmentPercent,
    PropTaskUri: propTaskUri,
    PropExpSchedUri: propExpSchedUri,
    PropBudPeriodFromDate: propBudPeriodFromDate,
    PropBudPeriodToDate: propBudPeriodToDate
  }
}

const mapBudgetDataToNodeData = (data: BudgetVsActualData[],
                                 nameFunc: (items: BudgetVsActualData[]) => string,
                                 lowBudgetThreshold: number,
                                 overBudgetThreshold: number,
                                 selectedPeriods: (string | number)[],
                                 includeCommitments: boolean): BudgetVsActualNode => {

  const distinctValues: string[] = distinct(data, 'PeriodUri');
  const groupedByPeriods = groupByProp(data, 'PeriodUri');
  const periods = [
    mapPeriodNodeData(data, 'Full Year', lowBudgetThreshold, overBudgetThreshold, includeCommitments)
  ]
  const apportionment = periods[0].ApportionmentPercent
  if (selectedPeriods.length == 0) periods.push({...periods[0], Name: 'Totals'});
  else if (selectedPeriods.includes('Totals')) {
    periods.push(mapPeriodNodeData(
      data.filter(x => selectedPeriods.includes(x.PeriodUri)),
      'Totals',
      lowBudgetThreshold,
      overBudgetThreshold,
      includeCommitments))
  }
  distinctValues.forEach(x => {
    if (selectedPeriods.length == 0 || selectedPeriods.includes(x)) {
      periods.push(mapPeriodNodeData(
        groupedByPeriods[x],
        x,
        lowBudgetThreshold,
        overBudgetThreshold,
        includeCommitments));
    }
  })

  return {
    Name: nameFunc ? nameFunc(data) : null,
    Code: data?.length > 0 ? data[0].ExpenseCode : '',
    ScheduleRefs: Array.from(new Set(data.map(x => x.ScheduleRef))),
    Periods: periods,
    Apportionment: apportionment
  }
}

export const getExportableData = (widgetId: number) =>
  createSelector(getFilteredData(widgetId), isLoading(widgetId), getYear(widgetId), getPropertyRef(widgetId), getPropertyName(widgetId), (data, isLoading, year, propertyRef, propertyName) => {
    if (isLoading || !data) {
      return [];
    }
    return data.map(x => {
        return {
          ...x,
          Year: year,
          PropertyRef: propertyRef,
          PropertyName: propertyName,
          VarianceAmount1: x.ActualPlusCommitment - x.Budget1,
          VarianceAmount2: x.ActualPlusCommitment - x.Budget2,
          VarianceAmount3: x.ActualPlusCommitment - x.Budget3,
          VariancePercent1: variancePercentageCalc(x.Budget1, x.VarianceAmount1),
          VariancePercent2: variancePercentageCalc(x.Budget2, x.VarianceAmount2),
          VariancePercent3: variancePercentageCalc(x.Budget3, x.VarianceAmount3)
        }
      }
    )
  });

  export const getPrintableData = (widgetId: number) =>
    createSelector(
        getFilteredData(widgetId), isLoading(widgetId), getYear(widgetId), getPropertyRef(widgetId),
        getPropertyName(widgetId), getSelectedPeriods(widgetId), getSelectedSchedules(widgetId), getAllSchedules(widgetId),
        getData(widgetId),
        (data, isLoading, year, propertyRef, propertyName, selectedPeriods, selectedScheduleRefs, allSchedules, allData) => {
      if (isLoading || !data) {
        return [];
      }

      var scheduleRefs = selectedScheduleRefs;
      if(!selectedScheduleRefs || selectedScheduleRefs.length === 0)
        scheduleRefs = allSchedules.map(x => x.ScheduleRef);

      let dataForBudgetPeriods: BudgetVsActualData[] = [];
      let selectedBudgetPeriods: (string | number)[] = [];

      selectedBudgetPeriods = selectedPeriods.filter(x => x !== "Full Year" && x !== "Totals");
      if(selectedBudgetPeriods.length === 0){
      }
      else if(selectedBudgetPeriods && selectedBudgetPeriods.length > 0){
        dataForBudgetPeriods = data.filter(x => selectedBudgetPeriods.includes(x.PeriodUri));
      }
      else{
        dataForBudgetPeriods = data;
      }

      var dataForFullYearPeriod: BudgetVsActualData[] = [];

      if((selectedPeriods && selectedPeriods.length === 0) || (selectedPeriods.length > 0 && selectedPeriods.includes("Full Year"))){

        scheduleRefs.forEach( s => {

          let dataForSchedule = allData.filter(x => x.ScheduleRef === s);
          let distinctExpenseCodesForSchedule = new Set(dataForSchedule.map(item => item.ExpenseCode));

          distinctExpenseCodesForSchedule.forEach(e => {
            let filteredData = allData.filter(x => x.ScheduleRef === s && x.ExpenseCode === e)
            let firstRow = filteredData.at(0);
            let scheduleLabel = allSchedules.find(allS => allS.ScheduleRef === s)?.ScheduleLabel;
            let scheduleName = scheduleLabel.substring(scheduleLabel.indexOf("- ") + 2);
            dataForFullYearPeriod.push({
              PeriodLabel: "Full Year",
              PeriodUri: 0,
              ScheduleName: scheduleName,
              ScheduleRef: s,
              ExpenseCode: e,
              ExpenseDescription: dataForSchedule.find(x => x.ExpenseCode === e).ExpenseDescription,
              Category: firstRow !== undefined ? (firstRow.Category ?? "") : "",
              Class: firstRow !== undefined ? (firstRow.Class ?? "") : "",
              Note: firstRow !== undefined ? (firstRow.Note ?? "") : "",
              PropTaskUri: firstRow !== undefined ? (firstRow.PropTaskUri ? firstRow.PropTaskUri : 0) : 0,
              PropExpSchedUri: firstRow !== undefined ? (firstRow.PropExpSchedUri === undefined ? 0 : firstRow.PropExpSchedUri) : 0,
              PropBudPeriodFromDate: firstRow !== undefined ? (firstRow.PropBudPeriodFromDate === undefined ? null : firstRow.PropBudPeriodFromDate) : null,
              PropBudPeriodToDate: firstRow !== undefined ? (firstRow.PropBudPeriodToDate === undefined ? null : firstRow.PropBudPeriodToDate) : null,
              Actual: filteredData.reduce((sum, x) => sum + x.Actual, 0),
              PreviousYearActual: filteredData.reduce((sum, x) => sum + x.PreviousYearActual, 0),
              Budget1: filteredData.reduce((sum, x) => sum + x.Budget1, 0),
              Budget2: filteredData.reduce((sum, x) => sum + x.Budget2, 0),
              Budget3: filteredData.reduce((sum, x) => sum + x.Budget3, 0),
              VarianceAmount1: filteredData.reduce((sum, x) => sum + x.VarianceAmount1, 0),
              VarianceAmount2: filteredData.reduce((sum, x) => sum + x.VarianceAmount2, 0),
              VarianceAmount3: filteredData.reduce((sum, x) => sum + x.VarianceAmount3, 0),
              VariancePercent1: filteredData.reduce((sum, x) => sum + x.VariancePercent1, 0),
              VariancePercent2: filteredData.reduce((sum, x) => sum + x.VariancePercent2, 0),
              VariancePercent3: filteredData.reduce((sum, x) => sum + x.VariancePercent3, 0),
              ActualPercent: filteredData.reduce((sum, x) => sum + x.ActualPercent, 0),
              Commitment: filteredData.reduce((sum, x) => sum + x.Commitment, 0),
              ActualPlusCommitment: filteredData.reduce((sum, x) => sum + x.ActualPlusCommitment, 0),
              ApportionmentPercent: filteredData.reduce((sum, x) => sum + x.ApportionmentPercent, 0)
            })
          })
        })
      }

      var dataForTotalsPeriod: BudgetVsActualData[] = [];

      if((selectedPeriods && selectedPeriods.length === 0) || (selectedPeriods.length > 0 && selectedPeriods.includes("Totals"))){

        scheduleRefs.forEach( s => {

          let dataForSchedule = dataForBudgetPeriods.filter(x => x.ScheduleRef === s);
          let distinctExpenseCodesForSchedule = new Set(dataForSchedule.map(item => item.ExpenseCode));

          distinctExpenseCodesForSchedule.forEach(e => {
            let filteredData = dataForBudgetPeriods.filter(x => x.ScheduleRef === s && x.ExpenseCode === e)
            let firstRow = filteredData.at(0);
            let scheduleLabel = allSchedules.find(allS => allS.ScheduleRef === s)?.ScheduleLabel;
            let scheduleName = scheduleLabel.substring(scheduleLabel.indexOf("- ") + 2);
            dataForTotalsPeriod.push({
              PeriodLabel: "Totals",
              PeriodUri: 0,
              ScheduleName: scheduleName,
              ScheduleRef: s,
              ExpenseCode: e,
              ExpenseDescription: dataForSchedule.find(x => x.ExpenseCode === e).ExpenseDescription,
              Category: firstRow !== undefined ? (firstRow.Category ?? "") : "",
              Class: firstRow !== undefined ? (firstRow.Class ?? "") : "",
              Note: firstRow !== undefined ? (firstRow.Note ?? "") : "",
              PropTaskUri: firstRow !== undefined ? (firstRow.PropTaskUri ? firstRow.PropTaskUri : 0) : 0,
              PropExpSchedUri: firstRow !== undefined ? (firstRow.PropExpSchedUri === undefined ? 0 : firstRow.PropExpSchedUri) : 0,
              PropBudPeriodFromDate: firstRow !== undefined ? (firstRow.PropBudPeriodFromDate === undefined ? null : firstRow.PropBudPeriodFromDate) : null,
              PropBudPeriodToDate: firstRow !== undefined ? (firstRow.PropBudPeriodToDate === undefined ? null : firstRow.PropBudPeriodToDate) : null,
              Actual: filteredData.reduce((sum, x) => sum + x.Actual, 0),
              PreviousYearActual: filteredData.reduce((sum, x) => sum + x.PreviousYearActual, 0),
              Budget1: filteredData.reduce((sum, x) => sum + x.Budget1, 0),
              Budget2: filteredData.reduce((sum, x) => sum + x.Budget2, 0),
              Budget3: filteredData.reduce((sum, x) => sum + x.Budget3, 0),
              VarianceAmount1: filteredData.reduce((sum, x) => sum + x.VarianceAmount1, 0),
              VarianceAmount2: filteredData.reduce((sum, x) => sum + x.VarianceAmount2, 0),
              VarianceAmount3: filteredData.reduce((sum, x) => sum + x.VarianceAmount3, 0),
              VariancePercent1: filteredData.reduce((sum, x) => sum + x.VariancePercent1, 0),
              VariancePercent2: filteredData.reduce((sum, x) => sum + x.VariancePercent2, 0),
              VariancePercent3: filteredData.reduce((sum, x) => sum + x.VariancePercent3, 0),
              ActualPercent: filteredData.reduce((sum, x) => sum + x.ActualPercent, 0),
              Commitment: filteredData.reduce((sum, x) => sum + x.Commitment, 0),
              ActualPlusCommitment: filteredData.reduce((sum, x) => sum + x.ActualPlusCommitment, 0),
              ApportionmentPercent: filteredData.reduce((sum, x) => sum + x.ApportionmentPercent, 0)
            })
          })
        })
      }

      return dataForFullYearPeriod.concat(dataForTotalsPeriod).concat(dataForBudgetPeriods).map(x => {
        return {
          ...x,
          Year: year,
          PropertyRef: propertyRef,
          PropertyName: propertyName,
          VariancePercent1: variancePercentageCalc(x.Budget1, x.VarianceAmount1),
          VariancePercent2: variancePercentageCalc(x.Budget2, x.VarianceAmount2),
          VariancePercent3: variancePercentageCalc(x.Budget3, x.VarianceAmount3)
        }
      });
    });

export const getTotal = (widgetId: number, includeCommitments: boolean) =>
  createSelector(
    getFilteredData(widgetId),
    isLoading(widgetId),
    getSelectedPeriods(widgetId),
    (data, isLoading, periods) => {
      if (isLoading || !data) {
        return undefined;
      }
      return mapBudgetDataToNodeData(data, null, 0, 0, periods, includeCommitments)
    });

export const mapNodes = (data: BudgetVsActualData[],
                         hierachyDefinition: BvAHierarchyModel[],
                         lowBudgetThreshold,
                         overBudgetThreshold,
                         periods: (string | number)[],
                         includeCommitments: boolean): TreeNode<BudgetVsActualNode>[] => {
  if (hierachyDefinition.length > 0) {
    const prop = hierachyDefinition.shift()
    const distinctValues: string[] = distinct(data, prop.field);
    if (prop.field != "ScheduleName") {
      distinctValues.sort();
    }
    const groupedData = groupBy(data, prop.field);
    return distinctValues.map(name => {
      return {
        data: mapBudgetDataToNodeData(groupedData[name],
                prop.nameFunc,
                lowBudgetThreshold,
                overBudgetThreshold,
                periods,
                includeCommitments),
        children: mapNodes(groupedData[name],
                [...hierachyDefinition],
                lowBudgetThreshold,
                overBudgetThreshold,
                periods,
                includeCommitments)
      }
    });
  }
  const groupedExpenses = groupBy(data, "ExpenseCode");
  const distinctExpenses: string[] = distinct(data, "ExpenseCode");
  distinctExpenses.sort();
  const expenseNameFunc = (items: BudgetVsActualData[]) => `${items[0].ExpenseCode} - ${items[0].ExpenseDescription}`;
  return distinctExpenses.map(d => <TreeNode<BudgetVsActualNode>>{
      data: mapBudgetDataToNodeData(groupedExpenses[d],
            expenseNameFunc,
            lowBudgetThreshold,
            overBudgetThreshold,
            periods,
            includeCommitments)
    });
};

export const getAllSchedules = (widgetId: number) =>
  createSelector(getData(widgetId), isLoading(widgetId), (data, isLoading) => {
    if (isLoading || !data) return [];
    return distinctByProperty(data, 'ScheduleRef').map(x => {
      return {
        ScheduleRef: x.ScheduleRef,
        ScheduleLabel: x.ScheduleRef + " - " + x.ScheduleName
      }
    });

  });

export const getLowBudgetThreshold = (widgetId: number) => createSelector(selectEntityByWidgetId(widgetId), (entity) => {
  return entity?.lowBudgetThreshold;
})

export const getOverBudgetThreshold = (widgetId: number) => createSelector(selectEntityByWidgetId(widgetId), (entity) => {
  return entity?.overBudgetThreshold;
})

export const getTreeNodeData = (widgetId: number,
                                widgetConfigId: number,
                                includeCommitments: boolean) =>
  createSelector(isLoading(widgetId),
    getFilteredData(widgetId),
    getLowBudgetThreshold(widgetId),
    getOverBudgetThreshold(widgetId),
    getSelectedPeriods(widgetId),
    getHierarchies(widgetConfigId),
    (isLoading,
     data,
     lowBudgetThreshold,
     overBudgetThreshold,
     periods,
     hierarchies) => {
      if (isLoading || !data || !hierarchies) return null;
      const config = [...hierarchies];
      return mapNodes(data, config, lowBudgetThreshold, overBudgetThreshold, periods, includeCommitments);
    });

export const getDataFilteredByPeriod = (widgetId: number) =>
  createSelector(isLoading(widgetId), getFilteredData(widgetId), getSelectedPeriods(widgetId), (isLoading, data, periods) => {
    if (isLoading || !data) return null;
    if (periods.filter(x => x != 'Full Year' && x != 'Totals').length == 0) return data;
    return data.filter(x => periods.includes(x.PeriodUri));
  })

export const groupDataForCharts = (widgetId: number) =>
  createSelector(isLoading(widgetId), getDataFilteredByPeriod(widgetId), getSelectedBudget(widgetId), (isLoading, data, selectedBudget) => {
    if (isLoading || !data) return null;
    return groupAndSum(data, ['ExpenseCode', 'ExpenseDescription', 'ScheduleName', 'Class', 'Note', 'ApportionmentPercent'], ['Actual', selectedBudget.BudgetField, selectedBudget.VarianceField, 'Commitment']) as object[];
  });

export const getChartData = (widgetId: number, widgetConfigId: number) =>
  createSelector(isLoading(widgetId), getSelectedBudget(widgetId), sortedData(widgetId), getHierarchies(widgetConfigId), getEffectiveIncludeCommitments(widgetId), (isLoading, selectedBudget, data, hierarchies, includeCommitments) => {
    if (isLoading || !data) return null;
    const hierachyFields = hierarchies.map(x => x.field);
    return {
      Labels: data.map(item => {
        const label = [];

        if (hierachyFields.includes('ScheduleName') && hierachyFields.includes('Class')) {
          label.push(`${item['ScheduleName'] ?? ""} ${item['Class'] != null ? '- ' + item['Class'] : ""}`);
        } else if (hierachyFields.includes('ScheduleName')) {
          label.push(item['ScheduleName'] ?? "");
        } else if (hierachyFields.includes('Class')) {
          label.push(item['Class'] ?? "");
        }

        label.push(`${item['ExpenseCode']} - ${item['ExpenseDescription']}`);
        return label;
      }),
      ActualData: data.map(item => item['Actual']),
      BudgetData: data.map(item => item[selectedBudget.BudgetField]),
      VarianceData: data.map(item => item[selectedBudget.VarianceField]),
      CommitmentData: data.map(item => includeCommitments ? (item['Commitment'] < 0 ? 0 : item['Commitment']) : 0),
      Note: data.map(item => {
        const note = [];
        note.push(item['Note'] ? `${item['Note']}` : '');
        return note;
      }),
      ApportionmentPercent: data.map(item => item['ApportionmentPercent'])
    }
  });

export const getFilteredChartData = (widgetId: number, widgetConfigId: number, searchString?: string) =>

  createSelector(isLoading(widgetId), getChartData(widgetId, widgetConfigId), (isLoading, data) => {

    if (!data || isLoading) return null;
    if (!searchString) return data;

    const labelContainsSearchString = (label) => label.some((element) => element != null && element.toLowerCase().includes(searchString.toLowerCase()));

    const filterData = (originalData) => originalData.filter((_, index) => labelContainsSearchString(data.Labels[index]));

    return {
      Labels: filterData(data.Labels),
      ActualData: filterData(data.ActualData),
      BudgetData: filterData(data.BudgetData),
      VarianceData: filterData(data.VarianceData),
      CommitmentData: filterData(data.CommitmentData),
      Note: filterData(data.Note),
      ApportionmentPercent: filterData(data.ApportionmentPercent)
    };
  });

export const getSelectedColumns = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loadingColumns) return null;
    return entity.columns;
  });

export const sortedData = (widgetId: number) =>
  createSelector(isLoading(widgetId), groupDataForCharts(widgetId), getSelectedSortOrder(widgetId), getSelectedBudget(widgetId), getEffectiveIncludeCommitments(widgetId), (isLoading, data, sortOrder, selectedBudget, includeCommitments) => {
    if (!data || isLoading) return null;
    if (!sortOrder) return data;

    const result = data.map(x => {
        const commitment = includeCommitments ? x['Commitment'] : 0;
        const actualPlusCommitment = includeCommitments ? x['ActualPlusCommitment'] : 0;
        return {
          ...x,
          ActualPercent: x[selectedBudget.BudgetField] ? x['Actual'] / x[selectedBudget.BudgetField] * 100 : 0,
          Commitment: commitment,
          ActualPlusCommitment: actualPlusCommitment,
          ActualPlusCommitmentPercent: x[selectedBudget.BudgetField] ? (includeCommitments ? x['ActualPlusCommitment'] / x[selectedBudget.BudgetField] * 100 : 0) : 0,
          CommitmentPercent: x[selectedBudget.BudgetField] ? commitment / x[selectedBudget.BudgetField] * 100 : 0,
        }
      }
    )

    defaultSortBehaviour(result, sortOrder.Property, sortOrder.Order)

    return result;
  });

export const getSelectedSortOrder = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading || !entity.selectedSortOrder) return null;
    return entity.selectedSortOrder;
  });

export const getEffectiveIncludeCommitments = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (!entity || entity.loading) return null;
    return entity.includeCommitments;
  });
